import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, qs } from '../lib/api'
import { dateTime, sentence } from '../lib/format'
import type { AuditEntry, ListResponse } from '../lib/types'
import { Page } from '../components/Shell'
import { DataTable, Toolbar, useSearch, type Column } from '../components/DataTable'
import { Chip, Panel } from '../components/ui'

const RANGES = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
]

/** Actions whose appearance in the log should draw the eye. */
const SENSITIVE = new Set([
  'auth.impersonate_start',
  'tenant.suspended',
  'tenant.terminated',
  'device.command',
  'user.delete',
  'vehicle.delete',
])

export function Audit() {
  const search = useSearch()
  const [range, setRange] = useState('30d')
  const [action, setAction] = useState('')
  const [offset, setOffset] = useState(0)
  const limit = 100

  const list = useQuery({
    queryKey: ['audit', search.debounced, range, action, offset],
    queryFn: () =>
      api<ListResponse<AuditEntry>>(
        `/audit${qs({ q: search.debounced, range, action, limit, offset })}`,
      ),
  })

  const columns: Column<AuditEntry>[] = [
    {
      key: 'at',
      header: 'When',
      width: '160px',
      render: (e) => <span className="tnum text-[12px]">{dateTime(e.at)}</span>,
    },
    {
      key: 'actor',
      header: 'Who',
      width: '200px',
      render: (e) => (
        <div className="min-w-0">
          <div className="truncate">{e.actorEmail ?? 'system'}</div>
          {e.impersonatorId && (
            <div className="text-[10px]" style={{ color: 'var(--text-warn)' }}>
              acting as this account
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      width: '190px',
      render: (e) => (
        <Chip tone={SENSITIVE.has(e.action) ? 'warn' : 'muted'}>{e.action}</Chip>
      ),
    },
    {
      key: 'summary',
      header: 'Detail',
      render: (e) => (
        <div className="min-w-0">
          <div className="truncate">{e.summary ?? sentence(e.action)}</div>
          {e.metadata && Object.keys(e.metadata).length > 0 && (
            <div className="text-[10px] text-[var(--color-ink-4)] truncate font-[var(--font-mono)]">
              {Object.entries(e.metadata)
                .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                .join('  ')}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'tenant',
      header: 'Account',
      width: '150px',
      render: (e) => e.tenantName ?? <span className="text-[var(--color-ink-4)]">—</span>,
    },
    {
      key: 'ip',
      header: 'From',
      numeric: true,
      width: '130px',
      render: (e) => <span className="text-[11px]">{e.ip ?? '—'}</span>,
    },
  ]

  return (
    <Page
      title="Audit log"
      subtitle="Every administrative action, across every account you can see"
    >
      <Toolbar search={search.value} onSearch={search.setValue} placeholder="Summary or email…">
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => {
                setRange(r.key)
                setOffset(0)
              }}
              className="h-[34px] px-3 rounded-[3px] border text-[12px] transition-colors"
              style={{
                borderColor: range === r.key ? 'var(--accent)' : 'var(--color-rule)',
                background: range === r.key ? 'var(--accent-dim)' : 'transparent',
                color: range === r.key ? 'var(--accent-text)' : 'var(--color-ink-3)',
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        <select className="field w-auto" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">All actions</option>
          <optgroup label="Sensitive">
            {[...SENSITIVE].map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </optgroup>
          <optgroup label="Routine">
            {[
              'auth.login',
              'auth.impersonate_stop',
              'tenant.create',
              'tenant.update',
              'user.create',
              'user.update',
              'vehicle.create',
              'vehicle.update',
              'device.create',
              'device.transfer',
              'geofence.create',
              'invoice.generate',
              'invoice.issue',
              'invoice.payment',
              'branding.update',
            ].map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </optgroup>
        </select>
      </Toolbar>

      <Panel padded={false}>
        <DataTable
          columns={columns}
          rows={list.data?.items}
          rowKey={(e) => String(e.id)}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          total={list.data?.total}
          limit={limit}
          offset={offset}
          onPage={setOffset}
          empty={{
            title: 'Nothing logged',
            body: 'No administrative actions in this period.',
          }}
        />
      </Panel>
    </Page>
  )
}
